import type { EndlessSubmitResult } from "./EndlessLeaderboardService";
import type { EndlessSubmissionMetrics } from "./types";

/**
 * What `EndlessResultScene`/`EndlessLeaderboardScene` should do next about
 * one pending Endless submission - the entire "must not discard a
 * completed run while consent setup is happening" contract in one small,
 * framework-free function, extracted out of the Scenes so its rules are
 * unit-testable without Phaser, a Scene, or a network call. Mirrors
 * `completionCoordinator.ts`'s `decideNamePrompt()` precedent: pure
 * decision (and, here, the actual submission call itself) lives here;
 * firing analytics stays Scene-side.
 *
 * PR34: a display name is no longer a precondition. An unnamed player's
 * run is submitted under their anonymous identity exactly like a named
 * one's, and shows as ANONYMOUS until SET NAME - at which point every
 * leaderboard read (which always joins the CURRENT `profiles.display_name`)
 * shows the new name on that same historical row automatically. The old
 * `"need-name"` outcome - which left the run only in memory, so PLAY
 * AGAIN or a refresh lost it for good - no longer exists.
 */
export type EndlessOnboardingAction = { type: "none" } | { type: "submitted"; result: EndlessSubmitResult };

export interface EndlessOnboardingDeps {
  /** Cheap, synchronous - mirrors `EndlessLeaderboardService.isSubmissionAvailable()`. */
  isSubmissionAvailable(): boolean;
  ensureSignedIn(): Promise<boolean>;
  submitResult(metrics: EndlessSubmissionMetrics): Promise<EndlessSubmitResult>;
}

/**
 * Decides - and, when the answer is "submit", actually performs - what
 * should happen to one completed Endless run's pending submission right
 * now:
 *  - no pending run (already submitted this run, or a QA preview that
 *    never had one) -> `"none"`, a pure no-op, no dependency called;
 *  - Online Features not enabled (never asked yet, or the player chose
 *    PLAY OFFLINE) -> `"none"` - never signs in, never touches the
 *    submission API, so declining truly transmits nothing (the run is
 *    already kept locally by `EndlessLeaderboardService.captureResult()`);
 *  - enabled but sign-in failed (a transient auth problem) -> `"none"`,
 *    left for the next retry rather than surfacing an error;
 *  - enabled and signed in, named or not -> actually submits and returns
 *    `"submitted"` with the server's own result.
 *
 * The same `metrics` reference can safely be passed to repeated calls
 * (e.g. once on the result screen's initial load, again after ENABLE) -
 * nothing here mutates it, and a caller that stops calling once it sees
 * `"submitted"` naturally submits the run exactly once. A duplicate call
 * is harmless anyway: `EndlessQueue` dedupes by `runId` and the server
 * treats a resubmitted `run_id` idempotently.
 */
export async function attemptEndlessSubmission(
  metrics: EndlessSubmissionMetrics | null | undefined,
  deps: EndlessOnboardingDeps,
): Promise<EndlessOnboardingAction> {
  if (!metrics) return { type: "none" };
  if (!deps.isSubmissionAvailable()) return { type: "none" };
  if (!(await deps.ensureSignedIn())) return { type: "none" };

  const result = await deps.submitResult(metrics);
  return { type: "submitted", result };
}
